import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import PageShell from '../components/layout/PageShell';
import {
  CreditCard, Users, Shield, Lock, Shuffle, Check,
  CalendarDays, Users2, Timer, Mail, Video,
  Loader2, AlertCircle, CheckCircle2, ExternalLink,
} from 'lucide-react';

const API_URL  = process.env.REACT_APP_API_URL;
const DURATION = 30;

const TEAMS = [
  { id: 'ciam',      label: 'CIAM Team',          desc: 'Auth0 / Okta CIS — Customer Identity',      Icon: CreditCard },
  { id: 'workforce', label: 'Workforce IAM Team',  desc: 'Okta WIC — Workforce Identity & Access',    Icon: Users },
  { id: 'iga',       label: 'IGA Team',            desc: 'Identity Governance & Administration',       Icon: Shield },
  { id: 'pam',       label: 'PAM Team',            desc: 'Privileged Access Management',               Icon: Lock },
  { id: 'mixed',     label: 'Mixed Session',       desc: 'Multiple focus areas — cross-team session', Icon: Shuffle },
];

const s = {
  teamGrid:     { display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '24px' },
  teamCard:     (sel) => ({ border: `1px solid ${sel ? '#fd5f0d' : '#e5e5e5'}`, borderRadius: '8px', padding: '12px 16px', cursor: 'pointer', background: sel ? '#fff4ec' : '#fff', display: 'flex', alignItems: 'center', gap: '12px', transition: 'all 0.15s' }),
  teamIconWrap: { width: '36px', height: '36px', borderRadius: '8px', background: '#f0f0ee', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#555' },
  teamLabel:    { fontSize: '13px', fontWeight: '600', marginBottom: '1px' },
  teamDesc:     { fontSize: '11px', color: '#888' },
  checkmark:    { marginLeft: 'auto', color: '#fd5f0d' },
  dayCol:       { marginBottom: '20px' },
  dayLabel:     { fontSize: '12px', fontWeight: '700', color: '#555', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' },
  slotsWrap:    { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  slotBtn:      (sel) => ({ padding: '6px 14px', borderRadius: '6px', fontSize: '12px', fontWeight: sel ? '700' : '400', cursor: 'pointer', border: `1px solid ${sel ? '#fd5f0d' : '#ddd'}`, background: sel ? '#fd5f0d' : '#fff', color: sel ? '#fff' : '#333', transition: 'all 0.12s' }),
  label:        { fontSize: '13px', fontWeight: '600', marginBottom: '6px', display: 'block', marginTop: '14px' },
  input:        { width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px', marginBottom: '4px', boxSizing: 'border-box' },
  textarea:     { width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px', minHeight: '72px', resize: 'vertical', boxSizing: 'border-box' },
  submitBtn:    (dis) => ({ marginTop: '20px', padding: '10px 24px', background: dis ? '#fcb185' : '#fd5f0d', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: '600', cursor: dis ? 'not-allowed' : 'pointer' }),
  loadingBox:   { textAlign: 'center', padding: '40px', color: '#888', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' },
  emptyBox:     { textAlign: 'center', padding: '32px', color: '#aaa', fontSize: '13px', border: '1px dashed #e0e0e0', borderRadius: '8px' },
  errorBanner:  { padding: '10px 14px', background: '#fdecea', border: '1px solid #f5c2be', borderRadius: '6px', color: '#b00020', fontSize: '13px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' },
  successBox:   { background: '#ebfbee', border: '1px solid #b2f2bb', borderRadius: '10px', padding: '24px', marginBottom: '24px' },
  successTitle: { fontSize: '16px', fontWeight: '700', color: '#2f9e44', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' },
  successMeta:  { fontSize: '13px', color: '#333', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px' },
  joinBtn:      { display: 'inline-flex', alignItems: 'center', gap: '8px', marginTop: '14px', padding: '10px 20px', background: '#5558ff', color: '#fff', borderRadius: '6px', fontSize: '13px', fontWeight: '600', textDecoration: 'none' },
  divider:      { borderTop: '1px solid #eee', margin: '20px 0' },
  stepLabel:    { fontSize: '11px', fontWeight: '700', color: '#fd5f0d', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '8px' },
  slotSummary:  { background: '#fff4ec', border: '1px solid #ffcda6', borderRadius: '8px', padding: '12px 16px', fontSize: '13px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' },

  // Active booking banner
  activeBanner: { background: '#fff4ec', border: '1px solid #ffcda6', borderRadius: '10px', padding: '20px 24px', marginBottom: '24px' },
  activeBannerTitle: { fontSize: '14px', fontWeight: '700', color: '#b23e06', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' },
  activeMeta:   { fontSize: '13px', color: '#333', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '8px' },
  blockedBox:   { background: '#f8f9fa', border: '1px solid #e5e5e5', borderRadius: '8px', padding: '16px', fontSize: '13px', color: '#666', textAlign: 'center', marginTop: '16px' },
  cancelBtn:    { marginTop: '14px', marginLeft: '12px', padding: '10px 20px', background: 'none', color: '#c92a2a', border: '1px solid #ffc9c9', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
};

function fmtTime(iso) {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
function fmtDate(dateKey) {
  const [y, m, d] = dateKey.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });
}
function fmtFull(iso) {
  return new Date(iso).toLocaleString([], { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}
function groupByLocalDate(grouped) {
  const local = {};
  for (const slots of Object.values(grouped)) {
    for (const slot of slots) {
      const localKey = new Date(slot.start).toLocaleDateString('en-CA');
      if (!local[localKey]) local[localKey] = [];
      local[localKey].push(slot);
    }
  }
  return Object.fromEntries(Object.entries(local).sort(([a], [b]) => a.localeCompare(b)));
}

function ActiveBookingBanner({ booking, getToken, onCancelled }) {
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const teamLabel = TEAMS.find(t => t.id === booking.team)?.label || booking.teamLabel;

  const handleCancel = async () => {
    if (!window.confirm('Cancel this meeting? This will remove it from the Teams calendar and cannot be undone.')) return;
    setCancelling(true);
    setCancelError('');
    try {
      const token = await getToken();
      const res = await fetch(`${API_URL}api/booking/${booking._id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: 'Cancelled' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to cancel meeting.');
      onCancelled();
    } catch (err) {
      setCancelError(err.message);
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div style={s.activeBanner}>
      <div style={s.activeBannerTitle}>
        <CalendarDays size={16} strokeWidth={1.75} color="#fd5f0d" />
        You have an upcoming meeting
      </div>
      <div style={s.activeMeta}><Users2 size={14} strokeWidth={1.75} color="#555" /> {teamLabel}</div>
      <div style={s.activeMeta}><CalendarDays size={14} strokeWidth={1.75} color="#555" /> {fmtFull(booking.preferredTime)}</div>
      <div style={s.activeMeta}><Timer size={14} strokeWidth={1.75} color="#555" /> {booking.durationMinutes || 30} minutes</div>
      {booking.topic && <div style={s.activeMeta}><Mail size={14} strokeWidth={1.75} color="#555" /> {booking.topic}</div>}
      {booking.joinUrl && (
        <a href={booking.joinUrl} target="_blank" rel="noreferrer" style={s.joinBtn}>
          <Video size={15} strokeWidth={2} /> Join Teams Meeting
        </a>
      )}
      {cancelError && <div style={{ ...s.errorBanner, marginTop: '14px' }}><AlertCircle size={15} strokeWidth={2} /> {cancelError}</div>}
      <button style={s.cancelBtn} onClick={handleCancel} disabled={cancelling}>
        {cancelling ? 'Cancelling…' : 'Cancel Meeting'}
      </button>
      <div style={s.blockedBox}>
        You can book a new session after your current meeting on <strong>{fmtFull(booking.preferredTime)}</strong>.
      </div>
    </div>
  );
}

export default function Booking() {
  const { getAccessTokenSilently, user } = useAuth0();

  const [activeBooking, setActiveBooking]   = useState(null);
  const [bookingLoading, setBookingLoading] = useState(true);

  const [selectedTeam, setSelectedTeam] = useState('');
  const [slots, setSlots]               = useState({});
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError]     = useState('');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [topic, setTopic]               = useState('');
  const [notes, setNotes]               = useState('');
  const [submitting, setSubmitting]     = useState(false);
  const [submitError, setSubmitError]   = useState('');
  const [confirmation, setConfirmation] = useState(null);

  const getToken = useCallback(() =>
    getAccessTokenSilently({ authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE } }),
  [getAccessTokenSilently]);

  // Check for active (future, non-cancelled) booking on load
  useEffect(() => {
    async function fetchMyBookings() {
      try {
        const token = await getToken();
        const res   = await fetch(`${API_URL}api/booking/my`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        const now  = new Date();
        const active = (data.bookings || []).find(b =>
          b.status !== 'Cancelled' &&
          new Date(b.preferredTime) > now
        );
        setActiveBooking(active || null);
      } catch (err) {
        console.error('Failed to fetch bookings:', err.message);
      } finally {
        setBookingLoading(false);
      }
    }
    fetchMyBookings();
  }, []);

  useEffect(() => {
    if (!selectedTeam) return;
    setSlotsLoading(true);
    setSlotsError('');
    setSelectedSlot(null);
    setSlots({});

    getToken().then(token =>
      fetch(`${API_URL}api/booking/slots?team=${selectedTeam}&duration=${DURATION}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
    ).then(r => r.json()).then(data => {
      if (data.error) throw new Error(data.error);
      setSlots(groupByLocalDate(data.slots || {}));
    }).catch(err => {
      setSlotsError(err.message);
    }).finally(() => setSlotsLoading(false));
  }, [selectedTeam]);

  const handleBook = async (e) => {
    e.preventDefault();
    if (!selectedSlot) { setSubmitError('Please select a time slot.'); return; }
    if (!topic)        { setSubmitError('Please enter a meeting topic.'); return; }
    setSubmitting(true);
    setSubmitError('');

    try {
      const token = await getToken();
      const res   = await fetch(`${API_URL}api/booking`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body:    JSON.stringify({
          team: selectedTeam, topic,
          start: selectedSlot.start, end: selectedSlot.end,
          durationMinutes: DURATION, notes,
          requesterEmail: user?.email || '', requesterName: user?.name || '',
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Booking failed.');
      // Graph's event response returns start/end as a naive "dateTime" string plus a
      // separate "timeZone" field (never a proper ISO 'Z' string) - passing that
      // straight into `new Date()` misinterprets it as browser-local time and shows
      // the wrong hour. We already know the correct instant from the slot the user
      // picked, so use that instead of trusting Graph's response shape for display.
      setConfirmation({
        ...data,
        teamLabel: TEAMS.find(t => t.id === selectedTeam)?.label,
        start: selectedSlot.start,
      });
      // Set as active booking so form hides
      setActiveBooking({
        _id: data.bookingId,
        team: selectedTeam,
        teamLabel: TEAMS.find(t => t.id === selectedTeam)?.label,
        preferredTime: selectedSlot.start,
        durationMinutes: DURATION,
        topic,
        joinUrl: data.joinUrl,
        status: 'Confirmed',
      });
      setSelectedTeam(''); setSelectedSlot(null);
      setTopic(''); setNotes(''); setSlots({});
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const localDates = Object.keys(slots).sort();

  return (
    <PageShell title="Book Time with RAAH" desc="Schedule a 30-minute Teams session with the right RAAH team.">

      {bookingLoading && (
        <div style={s.loadingBox}>
          <Loader2 size={16} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* Confirmation */}
      {!bookingLoading && confirmation && (
        <div style={s.successBox}>
          <div style={s.successTitle}>
            <CheckCircle2 size={20} strokeWidth={2} /> Meeting Confirmed!
          </div>
          <div style={s.successMeta}><CalendarDays size={14} strokeWidth={1.75} /> {fmtFull(confirmation.start)}</div>
          <div style={s.successMeta}><Users2 size={14} strokeWidth={1.75} /> {confirmation.teamLabel}</div>
          <div style={s.successMeta}><Timer size={14} strokeWidth={1.75} /> 30 minutes</div>
          <div style={s.successMeta}><Mail size={14} strokeWidth={1.75} /> A calendar invite has been sent to all attendees.</div>
          {confirmation.joinUrl && (
            <a href={confirmation.joinUrl} target="_blank" rel="noreferrer" style={s.joinBtn}>
              <Video size={15} strokeWidth={2} /> Join Teams Meeting
            </a>
          )}
        </div>
      )}

      {/* Active booking banner — blocks new booking */}
      {!bookingLoading && activeBooking && (
        <ActiveBookingBanner booking={activeBooking} getToken={getToken} onCancelled={() => { setActiveBooking(null); setConfirmation(null); }} />
      )}

      {/* Booking form — only show if no active booking and no fresh confirmation */}
      {!bookingLoading && !activeBooking && !confirmation && (
        <>
          <div style={s.stepLabel}>Step 1 — Select Team</div>
          <div style={s.teamGrid}>
            {TEAMS.map(({ id, label, desc, Icon }) => (
              <div key={id} style={s.teamCard(selectedTeam === id)} onClick={() => setSelectedTeam(id)}>
                <div style={s.teamIconWrap}><Icon size={18} strokeWidth={1.75} /></div>
                <div>
                  <div style={s.teamLabel}>{label}</div>
                  <div style={s.teamDesc}>{desc}</div>
                </div>
                {selectedTeam === id && <span style={s.checkmark}><Check size={16} strokeWidth={2.5} /></span>}
              </div>
            ))}
          </div>

          {selectedTeam && (
            <>
              <div style={s.divider} />
              <div style={s.stepLabel}>Step 2 — Pick a Time</div>
              <div style={{ fontSize: '12px', color: '#888', marginBottom: '14px' }}>All sessions are 30 minutes.</div>

              {slotsLoading && (
                <div style={s.loadingBox}>
                  <Loader2 size={16} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />
                  Checking availability…
                </div>
              )}
              {slotsError && <div style={s.errorBanner}><AlertCircle size={15} strokeWidth={2} /> {slotsError}</div>}
              {!slotsLoading && !slotsError && localDates.length === 0 && (
                <div style={s.emptyBox}>No available slots in the next 7 days.<br />Please try a different team or check back soon.</div>
              )}
              {!slotsLoading && !slotsError && localDates.length > 0 && (
                <div>
                  {localDates.map(dateKey => (
                    <div key={dateKey} style={s.dayCol}>
                      <div style={s.dayLabel}>{fmtDate(dateKey)}</div>
                      <div style={s.slotsWrap}>
                        {slots[dateKey].map(slot => {
                          const isSel = selectedSlot?.start === slot.start;
                          return (
                            <button key={slot.start} style={s.slotBtn(isSel)} onClick={() => setSelectedSlot(slot)}>
                              {fmtTime(slot.start)}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {selectedSlot && (
            <>
              <div style={s.divider} />
              <div style={s.stepLabel}>Step 3 — Meeting Details</div>
              <div style={s.slotSummary}>
                <CalendarDays size={14} strokeWidth={1.75} color="#3b5bdb" />
                <strong>{fmtFull(selectedSlot.start)}</strong>
                · 30 min · {TEAMS.find(t => t.id === selectedTeam)?.label}
              </div>
              {submitError && <div style={s.errorBanner}><AlertCircle size={15} strokeWidth={2} /> {submitError}</div>}
              <form onSubmit={handleBook}>
                <span style={s.label}>Meeting Topic *</span>
                <input style={s.input} type="text" placeholder="e.g. Auth0 migration scoping call"
                  value={topic} onChange={e => setTopic(e.target.value)} required />
                <span style={s.label}>Context / Notes</span>
                <textarea style={s.textarea} placeholder="Any context to share ahead of the meeting"
                  value={notes} onChange={e => setNotes(e.target.value)} />
                <button type="submit" style={s.submitBtn(submitting)} disabled={submitting}>
                  {submitting ? 'Creating meeting…' : 'Confirm Booking'}
                </button>
              </form>
            </>
          )}
        </>
      )}
    </PageShell>
  );
}